import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Mail, MapPin } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. Thank you.");
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="bg-navy text-pearl py-20" data-testid="contact-hero">
        <div className="section-container">
          <p className="eyebrow text-gold">Get in touch</p>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl">We'd love to hear from you.</h1>
        </div>
      </section>

      <section className="py-20 bg-pearl">
        <div className="section-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow">Reach us</p>
            <h2 className="mt-3 font-display text-3xl text-navy">Questions, suggestions, or collaborations.</h2>
            <div className="mt-8 space-y-5 text-navy">
              <div className="flex items-start gap-3">
                <Mail className="text-gold mt-1" size={18} />
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Email</div>
                  <div className="font-medium">info@ambedkaritebuddhistglobalnetworkuk.com</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="text-gold mt-1" size={18} />
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Region</div>
                  <div className="font-medium">United Kingdom · cloud-based, everywhere you are.</div>
                </div>
              </div>
            </div>
            <blockquote className="mt-10 border-l-4 border-gold pl-6 font-display italic text-xl text-navy">
              "A great man is different from an eminent one in that he is ready to be the servant of the society."
              <span className="block mt-2 not-italic text-sm text-slate-500">— Dr. B.R. Ambedkar</span>
            </blockquote>
          </div>
          <form
            onSubmit={submit}
            className="lg:col-span-7 bg-white border border-navy/10 p-8 sm:p-10 rounded-sm"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="contact-name" />
              <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="contact-email" />
            </div>
            <Input label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} required testid="contact-subject" className="mt-5" />
            <label className="block mt-5">
              <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Message</span>
              <textarea
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                data-testid="contact-message"
                className="mt-2 w-full border border-navy/15 px-4 py-3 rounded-sm focus:outline-none focus:border-gold"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="mt-6 px-8 py-3 bg-navy text-pearl hover:bg-navy-light rounded-sm font-medium disabled:opacity-60"
              data-testid="contact-submit"
            >
              {loading ? "Sending…" : sent ? "Sent. Send another?" : "Send message"}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}

function Input({ label, value, onChange, type = "text", required, testid, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs uppercase tracking-[0.18em] text-slate-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-2 w-full border border-navy/15 px-4 py-3 rounded-sm focus:outline-none focus:border-gold"
      />
    </label>
  );
}
